/**
 * LaunchStatsPage — Feb 2026 (Phase 6.5)
 *
 * Standalone SA dashboard tile view for the Reviews program launch (Track B).
 * Answers "is the launch landing?" in a single glance:
 *   * REVIEWS SUBMITTED TODAY (last 24h)
 *   * PENDING MODERATION QUEUE DEPTH
 *   * POINTS AWARDED (review + review_like sources)
 *   * FIRE REVIEWS (rating >= 4)
 *
 * Every tile shows a trend arrow (today vs yesterday) and the all-time
 * counter. Uses the aesthetic of the existing terminal-style SA pages
 * (mono font, phosphor green accents, purple grid).
 */
import { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, RefreshCw, Star, Clock, Coins, Flame,
  TrendingUp, TrendingDown, ExternalLink, Users, UserPlus, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TrendIndicator({ current, previous }) {
  if (previous === 0 && current === 0) {
    return <span className="text-[#4B6B52] text-[10px]">— no history</span>;
  }
  if (previous === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[#39FF14] text-[10px]" data-testid="trend-up">
        <TrendingUp className="w-3 h-3" /> NEW
      </span>
    );
  }
  const delta = current - previous;
  const pct = Math.round((delta / previous) * 100);
  if (delta === 0) return <span className="text-[#4B6B52] text-[10px]">flat vs 24h ago</span>;
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[#39FF14] text-[10px]" data-testid="trend-up">
        <TrendingUp className="w-3 h-3" /> +{pct}% vs yesterday
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[#FF3B30] text-[10px]" data-testid="trend-down">
      <TrendingDown className="w-3 h-3" /> {pct}% vs yesterday
    </span>
  );
}

function Tile({ testid, icon: Icon, accent, label, value, trend, sub, cta }) {
  return (
    <div
      className="border p-4 bg-[#050003] flex flex-col gap-1.5"
      style={{ borderColor: '#2A0F3D', borderLeft: `2px solid ${accent}` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#6B8E73]">
          {label}
        </span>
      </div>
      <div
        className="font-mono font-bold text-3xl"
        style={{ color: accent }}
        data-testid={`${testid}-value`}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[10px] font-mono text-[#4B6B52]">{sub}</div>
      )}
      {trend && <div>{trend}</div>}
      {cta && (
        <Link
          to={cta.to}
          className="text-[10px] font-mono uppercase hover:underline mt-1"
          style={{ color: accent }}
          data-testid={`${testid}-cta`}
        >
          {cta.label} <ExternalLink className="inline w-3 h-3 ml-0.5" />
        </Link>
      )}
    </div>
  );
}

export function LaunchStatsPage() {
  // Feb 2026 — React Query (Phase 2). 60s auto-refresh handled by
  // refetchInterval so back-nav to this page paints instantly from cache
  // and the tiles keep ticking without a manual interval.
  const query = useQuery({
    queryKey: ['dispatch', 'launch-stats'],
    queryFn: async () => {
      try {
        const { data: d } = await axios.get(
          `${API_URL}/api/admin/launch-stats`,
          { withCredentials: true },
        );
        return d;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load launch stats');
        throw err;
      }
    },
    refetchInterval: 60_000,
  });
  const data = query.data || null;
  const loading = query.isLoading;
  const load = useCallback(() => query.refetch(), [query]);

  const reviews = data?.reviews || {};
  const referrals = data?.referrals || {};
  const today = reviews.today || {};
  const yday = reviews.yesterday || {};
  const queue = reviews.queue || {};
  const all = reviews.alltime || {};
  const rToday = referrals.today || {};
  const rYday = referrals.yesterday || {};
  const rSnap = referrals.snapshot || {};
  const rAll = referrals.alltime || {};

  return (
    <div className="min-h-screen bg-[#0a0510] text-[#A3C7A8]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="launch-stats-page">
        <div className="flex items-center gap-3">
          <Link to="/dispatch" data-testid="launch-stats-back">
            <Button
              variant="ghost"
              size="sm"
              className="text-[#A3C7A8] hover:bg-[rgba(57,255,20,0.06)]"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> back
            </Button>
          </Link>
          <h1 className="font-mono text-lg text-[#39FF14]">
            &gt; LAUNCH STATS <span className="text-[#6B8E73]">/ GROWTH</span>
          </h1>
          <div className="flex-1" />
          <Button
            onClick={load}
            variant="ghost"
            size="sm"
            className="text-[#A3C7A8]"
            data-testid="launch-stats-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            refresh
          </Button>
        </div>

        <p className="text-[10px] font-mono text-[#4B6B52] uppercase tracking-wider">
          // TRACK B (Reviews) + REFERRAL PROGRAM. Auto-refreshes every 60s.
        </p>

        {loading && !data ? (
          <div className="text-[#4B6B52] font-mono text-xs">// loading…</div>
        ) : !data ? (
          <div className="text-[#FF3B30] font-mono text-xs">// no data</div>
        ) : (
          <>
            {/* Section header — REVIEWS */}
            <div className="flex items-center gap-2">
              <Star className="w-3.5 h-3.5 text-[#FFD60A]" />
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#FFD60A]">
                // TRACK B — REVIEWS
              </p>
              <Link
                to="/dispatch/admin/reviews"
                className="ml-auto text-[10px] font-mono text-[#FFD60A] hover:underline"
                data-testid="reviews-open-moderation"
              >
                MODERATION QUEUE <ExternalLink className="inline w-3 h-3 ml-0.5" />
              </Link>
            </div>

            {/* Primary tiles — today's snapshot */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile
                testid="launch-stats-reviews-today"
                icon={Star}
                accent="#FFD60A"
                label="REVIEWS TODAY"
                value={today.submitted ?? 0}
                trend={<TrendIndicator current={today.submitted || 0} previous={yday.submitted || 0} />}
                sub={`${all.reviews ?? 0} all-time · ${all.approved ?? 0} approved`}
              />
              <Tile
                testid="launch-stats-pending-queue"
                icon={Clock}
                accent="#FFB020"
                label="PENDING MODERATION"
                value={queue.pending ?? 0}
                sub={queue.pending > 0 ? 'reviews waiting for your call' : 'queue clear — great work'}
                cta={queue.pending > 0 ? { to: '/dispatch/admin/reviews', label: 'OPEN QUEUE' } : null}
              />
              <Tile
                testid="launch-stats-points-awarded"
                icon={Coins}
                accent="#00F0FF"
                label="POINTS AWARDED TODAY"
                value={today.points_awarded ?? 0}
                trend={<TrendIndicator current={today.points_awarded || 0} previous={yday.points_awarded || 0} />}
                sub={`${all.points_awarded ?? 0} pts all-time`}
              />
              <Tile
                testid="launch-stats-fire-count"
                icon={Flame}
                accent="#FF3B30"
                label="FIRE REVIEWS TODAY"
                value={today.fire ?? 0}
                trend={<TrendIndicator current={today.fire || 0} previous={yday.fire || 0} />}
                sub={`${all.fire ?? 0} all-time (rating ≥ 4)`}
              />
            </div>

            {/* Breakdown — smaller strip */}
            <div className="border border-[#2A0F3D] bg-[#050003] p-4">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#6B8E73] mb-3">
                // LAST 24H DETAILED
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono text-xs">
                <div>
                  <div className="text-[10px] text-[#4B6B52] uppercase">Submitted</div>
                  <div className="text-lg text-[#A3C7A8] font-bold" data-testid="detail-submitted">
                    {today.submitted ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#4B6B52] uppercase">Approved</div>
                  <div className="text-lg text-[#39FF14] font-bold" data-testid="detail-approved">
                    {today.approved ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#4B6B52] uppercase">Rejected</div>
                  <div className="text-lg text-[#FF3B30] font-bold" data-testid="detail-rejected">
                    {today.rejected ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#4B6B52] uppercase">Fire (≥4★)</div>
                  <div className="text-lg text-[#FF3B30] font-bold" data-testid="detail-fire">
                    {today.fire ?? 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Launch Stats v2 (Feb 2026) — Review Velocity (7-day bar strip) */}
            <div className="border border-[#2A0F3D] bg-[#050003] p-4" data-testid="review-velocity-strip">
              <div className="flex items-center gap-2 mb-3">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#6B8E73]">
                  // REVIEW VELOCITY / LAST 7 DAYS
                </p>
                <span className="text-[10px] font-mono text-[#4B6B52]">
                  peak {reviews.velocity_peak ?? 0}/day
                </span>
              </div>
              <div className="flex items-end gap-1.5 h-24">
                {(reviews.velocity_7d || []).map((b) => {
                  const peak = Math.max(reviews.velocity_peak || 0, 1);
                  const pct = Math.max((b.count / peak) * 100, b.count > 0 ? 4 : 0);
                  const isToday = b.day === (reviews.velocity_7d?.slice(-1)?.[0]?.day);
                  return (
                    <div
                      key={b.date}
                      className="flex-1 flex flex-col items-center gap-1"
                      data-testid={`velocity-bar-${b.date}`}
                      title={`${b.date}: ${b.count} review${b.count === 1 ? '' : 's'}`}
                    >
                      <div className="w-full flex-1 flex items-end">
                        <div
                          className="w-full rounded-none transition-all"
                          style={{
                            height: `${pct}%`,
                            backgroundColor: isToday ? '#FFD60A' : '#39FF14',
                            opacity: b.count > 0 ? 1 : 0.15,
                            minHeight: b.count > 0 ? '4px' : '2px',
                          }}
                        />
                      </div>
                      <div className="text-[9px] font-mono text-[#4B6B52]">{b.day.toUpperCase()}</div>
                      <div className="text-[10px] font-mono text-[#A3C7A8] font-bold" data-testid={`velocity-count-${b.date}`}>
                        {b.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Launch Stats v2 (Feb 2026) — Top Fired Reviews (last 7 days) */}
            <div className="border border-[#2A0F3D] bg-[#050003] p-4" data-testid="top-fired-reviews">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-3.5 h-3.5 text-[#FF3B30]" />
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF3B30]">
                  // TOP 🔥 REVIEWS / LAST 7 DAYS
                </p>
                <span className="ml-auto text-[9px] font-mono text-[#4B6B52]">
                  marketing-copy candidates
                </span>
              </div>
              {(reviews.top_fired_7d || []).length === 0 ? (
                <div className="text-[10px] font-mono text-[#4B6B52] py-2" data-testid="top-fired-empty">
                  // no 🔥 reactions yet in the last 7 days.
                  Ping loyal customers to drop a 🔥 on reviews they agree with.
                </div>
              ) : (
                <ol className="space-y-2">
                  {reviews.top_fired_7d.map((r, i) => (
                    <li
                      key={r.id}
                      className="border-l-2 border-[#FF3B30]/50 pl-3 py-1"
                      data-testid={`top-fired-row-${i}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-[#FF3B30] font-bold">
                          {r.like_count}🔥
                        </span>
                        <span className="font-mono text-[11px] text-[#FFD60A]">
                          {'★'.repeat(Math.max(0, Math.min(5, r.rating)))}
                        </span>
                        <span className="font-mono text-xs text-[#EAE7E1] truncate max-w-[240px]">
                          {r.product_name}
                        </span>
                        <span className="text-[10px] font-mono text-[#4B6B52]">— {r.author_name}</span>
                      </div>
                      {r.snippet && (
                        <div className="text-[11px] text-[#A3C7A8] mt-1 italic">
                          &ldquo;{r.snippet}&rdquo;
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Section header — REFERRALS */}
            <div className="flex items-center gap-2 mt-6" data-testid="referrals-section">
              <Users className="w-3.5 h-3.5 text-[#FF9F0A]" />
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF9F0A]">
                // REFERRAL PROGRAM
              </p>
              <Link
                to="/dispatch/admin/leaderboards?tab=referrers"
                className="ml-auto text-[10px] font-mono text-[#FF9F0A] hover:underline"
                data-testid="referrals-open-leaderboard"
              >
                OPEN LEADERBOARD <ExternalLink className="inline w-3 h-3 ml-0.5" />
              </Link>
            </div>

            {/* Referrals tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile
                testid="launch-stats-referrals-converted"
                icon={Trophy}
                accent="#FF9F0A"
                label="CONVERTED TODAY"
                value={rToday.converted ?? 0}
                trend={<TrendIndicator current={rToday.converted || 0} previous={rYday.converted || 0} />}
                sub={`${rAll.converted ?? 0} all-time`}
              />
              <Tile
                testid="launch-stats-referrals-signups"
                icon={UserPlus}
                accent="#39FF14"
                label="SIGNUPS TODAY (via code)"
                value={rToday.signups ?? 0}
                trend={<TrendIndicator current={rToday.signups || 0} previous={rYday.signups || 0} />}
                sub="new customers using a referral code"
              />
              <Tile
                testid="launch-stats-referrals-pending"
                icon={Clock}
                accent="#FFB020"
                label="PENDING INVITEES"
                value={rSnap.pending ?? 0}
                sub={rSnap.pending > 0 ? "signed up, haven't ordered yet" : "no pending — all invitees converted"}
                cta={rSnap.pending > 0 ? { to: '/dispatch/admin/leaderboards?tab=referrers', label: 'SEE WHO' } : null}
              />
              <Tile
                testid="launch-stats-referrals-points"
                icon={Coins}
                accent="#00F0FF"
                label="REFERRAL PTS TODAY"
                value={rToday.points_awarded ?? 0}
                trend={<TrendIndicator current={rToday.points_awarded || 0} previous={rYday.points_awarded || 0} />}
                sub={`${rAll.points_awarded ?? 0} pts all-time`}
              />
            </div>

            <p className="text-[10px] font-mono text-[#4B6B52] text-right">
              Generated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default LaunchStatsPage;
